import React, { ChangeEvent, FC } from "react"
import {
    FilterExpression,
    UnaryFilterOperator,
    MultipleValueFilterOperator,
    ConstFilterOperator,
    BinaryFilterOperator
} from "../../../Models/Client/FilterExpression"
import {
    ExclusiveChoiceDropdown,
    MultipleChoiceDropdown,
    SingleLineTextInput,
    DateTime,
    AddButton
} from "dash-styled"
import {
    FilterPredicate,
    isActionsPredicate,
    isBinaryPredicate,
    isFeedbackMessagePredicate,
    isMultiplePredicate,
    isUnaryPredicate,
    PredicateInputComponentProps
} from "./FilterPredicate"
import { FieldFilter } from "./FieldFilter"
import { FilterOption } from "./FilterOption"

export type FilterFields<M> = Partial<
    {
        [K in keyof M]: FilterOption<M[K]> | undefined
    }
>

interface FilterComposerProps<M> {
    value: FilterExpression<M>[]
    fields: FilterFields<M>
    defaultFilter: FilterExpression<M>
    className?: string
    compact?: boolean
    addFirstText?: string
    addAnotherText?: string
    onChange: (value: FilterExpression<M>[]) => void
}

const And: FC<{ isPlaceholder: boolean; className?: string }> = ({
    isPlaceholder,
    className
}) => (
    <span
        role={isPlaceholder ? "presentation" : undefined}
        className={`${className ?? ""} mr-2 ${
            isPlaceholder ? "invisible" : ""
        }`}
    >
        and
    </span>
)

export function FilterComposer<M>({
    value,
    fields,
    className,
    defaultFilter,
    compact = false,
    addFirstText = "Add a filter",
    addAnotherText = "Add another filter",
    onChange
}: FilterComposerProps<M>) {
    const availableFields = getAvailableFields(fields, value)
    const filterToAdd = findFilterToAdd(availableFields, fields, defaultFilter)
    const handleAdd =
        filterToAdd && (() => onChange(value.concat([filterToAdd])))
    const addButtonText = value.length === 0 ? addFirstText : addAnotherText
    const renderFieldFilter = (f: FilterExpression<M>, index: number) => {
        const isLastItem = index === value.length - 1
        const handleChange = (v?: FilterExpression<M>) =>
            onChange(
                v === undefined
                    ? value.filter((_, i) => i !== index)
                    : value.map((vv, i) => (i === index ? v : vv))
            )

        return (
            <FieldFilter<M>
                compact={compact}
                key={index}
                filter={f}
                availableFields={availableFields}
                fields={fields}
                onChange={handleChange}
                isFirstItem={index === 0}
                isLastItem={isLastItem}
                className={compact ? "mb-2" : isLastItem ? "mb-4" : ""}
            />
        )
    }

    return (
        <div className={className}>
            {value.map(renderFieldFilter)}
            {compact ? (
                <div className="flex justify-start items-center">
                    <And isPlaceholder={true} />
                    <AddButton
                        className="mb-2"
                        testId="add-filter-button"
                        text={addButtonText}
                        onClick={handleAdd}
                    />
                </div>
            ) : (
                <AddButton
                    testId="add-filter-button"
                    text={addButtonText}
                    onClick={handleAdd}
                />
            )}
        </div>
    )
}

interface EnumFilterOptions {
    label: string
    operator: UnaryFilterOperator | MultipleValueFilterOperator
    isMultiple?: boolean
}

export function enumFilter<V>(
    label: string,
    choices: { [label: string]: V },
    comparisonFn: (one: V, another: V) => boolean = (a, b) => a === b,
    allowMultiple: boolean = true
) {
    const filterOptions: EnumFilterOptions[] = [
        {
            label: "is",
            operator: UnaryFilterOperator.eq
        } as EnumFilterOptions
    ].concat(
        allowMultiple
            ? [
                  {
                      label: "is one of",
                      operator: MultipleValueFilterOperator.isOneOf,
                      isMultiple: true
                  }
              ]
            : []
    )
    const [values, labels]: [V[], string[]] = Object.entries(choices).reduce(
        ([passedValues, passedLabels], [l, v]) => [
            passedValues.concat([v]),
            passedLabels.concat([l])
        ],
        [[], []] as [V[], string[]]
    )
    const MultipleInput: FC<PredicateInputComponentProps<V[]>> = ({
        value,
        onChange
    }) => {
        const handleChange = (index: number) => {
            const chosen = values[index]

            onChange(
                value.some(v => comparisonFn(v, chosen))
                    ? value.filter(v => !comparisonFn(v, chosen))
                    : value.concat([chosen])
            )
        }

        return (
            <MultipleChoiceDropdown
                className="flex-grow"
                testId="filter-value"
                choices={labels}
                selectedIndices={values.flatMap((choice, i) =>
                    value.some(v => comparisonFn(v, choice)) ? [i] : []
                )}
                onClick={handleChange}
                size="sm"
            />
        )
    }
    const SingleInput: FC<PredicateInputComponentProps<V>> = ({
        value,
        onChange
    }) => {
        const handleChange = (index: number) => onChange(values[index])

        return (
            <ExclusiveChoiceDropdown
                className="flex-grow"
                testId="filter-value"
                choices={labels}
                selectedIndex={values.findIndex(v => comparisonFn(v, value))}
                onClick={handleChange}
                size="sm"
            />
        )
    }

    const predicates = filterOptions.map(option => {
        return {
            label: option.label,
            operator: option.operator,
            defaultValue: option.isMultiple ? [] : values[0],
            component: option.isMultiple ? MultipleInput : SingleInput
        } as FilterPredicate<V>
    })

    return {
        label,
        allowMultiple: false,
        predicates
    }
}

export function stringFilter<T>(
    label: string,
    toString: (v: T) => string,
    fromString: (v: string) => T,
    defaultValue: T,
    operator: UnaryFilterOperator.contains | UnaryFilterOperator.eq,
    operatorLabel: string
): FilterOption<T> {
    const Input: FC<PredicateInputComponentProps<T>> = ({
        value,
        onChange
    }) => {
        const handleChange = (e: ChangeEvent<HTMLInputElement>) =>
            onChange(fromString(e.target.value))

        return (
            <SingleLineTextInput
                className="flex-grow"
                testId="filter-value"
                autoComplete="off"
                size="sm"
                type="text"
                transparentBg={true}
                value={toString(value)}
                onChange={handleChange}
            />
        )
    }

    return {
        label,
        allowMultiple: false,
        predicates: [
            {
                label: operatorLabel,
                operator,
                defaultValue,
                component: Input
            }
        ]
    }
}

export function dateTimeFilter(
    label: string,
    defaultValue: string
): FilterOption<string> {
    const Input: FC<PredicateInputComponentProps<string>> = ({
        value,
        onChange
    }) => {
        return (
            <DateTime
                className="flex-grow"
                value={isNaN(Number(new Date(value))) ? undefined : value}
                inputClassName="shadow-none"
                onChange={onChange}
            />
        )
    }

    return {
        label,
        allowMultiple: true,
        predicates: [
            {
                label: "after",
                operator: UnaryFilterOperator.ge,
                defaultValue,
                component: Input
            },
            {
                label: "before",
                operator: UnaryFilterOperator.le,
                defaultValue,
                component: Input
            }
        ]
    }
}

interface NumberFilterOptions<T> {
    toNumber: (v: T) => number | null
    fromNumber: (v: number) => T
    largerThanLabel?: string
    smallerThanLabel?: string
}

export function numberFilter<T>(
    label: string,
    {
        toNumber,
        fromNumber,
        largerThanLabel = "is higher than",
        smallerThanLabel = "is lower than"
    }: NumberFilterOptions<T>
): FilterOption<T> {
    const Input: FC<PredicateInputComponentProps<T>> = ({
        value,
        onChange
    }) => {
        const handleChange = (e: ChangeEvent<HTMLInputElement>) =>
            onChange(fromNumber(parseFloat(e.target.value)))

        const displayValue = toNumber(value)
        return (
            <SingleLineTextInput
                className="flex-grow"
                testId="filter-value"
                autoComplete="off"
                size="sm"
                type="number"
                transparentBg={true}
                value={displayValue === null ? "" : String(displayValue)}
                onChange={handleChange}
            />
        )
    }
    const BetweenInput: FC<PredicateInputComponentProps<[T, T]>> = ({
        value,
        onChange
    }) => {
        const handleChangeLower = (e: ChangeEvent<HTMLInputElement>) =>
            onChange([fromNumber(parseFloat(e.target.value)), value[1]])
        const handleChangeHigher = (e: ChangeEvent<HTMLInputElement>) =>
            onChange([value[0], fromNumber(parseFloat(e.target.value))])

        return (
            <div className="flex items-center">
                <SingleLineTextInput
                    size="sm"
                    type="number"
                    className="flex-grow"
                    autoComplete="off"
                    transparentBg={true}
                    value={String(toNumber(value[0]))}
                    onChange={handleChangeLower}
                />
                <div className="mx-2">and</div>
                <SingleLineTextInput
                    size="sm"
                    type="number"
                    className="flex-grow"
                    autoComplete="off"
                    transparentBg={true}
                    value={String(toNumber(value[1]))}
                    onChange={handleChangeHigher}
                />
            </div>
        )
    }
    const defaultValue = fromNumber(NaN)

    return {
        label,
        allowMultiple: false,
        predicates: [
            {
                label: largerThanLabel,
                operator: UnaryFilterOperator.ge,
                defaultValue,
                component: Input
            },
            {
                label: smallerThanLabel,
                operator: UnaryFilterOperator.le,
                defaultValue,
                component: Input
            },
            {
                label: "is between",
                operator: BinaryFilterOperator.between,
                defaultValue: [defaultValue, defaultValue],
                component: BetweenInput
            }
        ]
    }
}

export function findFilterToAdd<M>(
    availableFields: (keyof M)[],
    fields: FilterFields<M>,
    defaultFilter: FilterExpression<M>
): FilterExpression<M> | undefined {
    if (availableFields.includes(defaultFilter.fieldName)) {
        return defaultFilter
    }

    for (const fieldName of availableFields) {
        const options = fields[fieldName]

        if (options === undefined || options.predicates.length === 0) {
            continue
        }

        const predicate = options.predicates[0]

        return isMultiplePredicate(predicate)
            ? {
                  fieldName,
                  operator: predicate.operator,
                  value: predicate.defaultValue
              }
            : isUnaryPredicate(predicate)
            ? {
                  fieldName,
                  operator: predicate.operator,
                  value: predicate.defaultValue
              }
            : isBinaryPredicate(predicate)
            ? {
                  fieldName,
                  operator: predicate.operator,
                  value: predicate.defaultValue
              }
            : predicate.operator === ConstFilterOperator.eq
            ? {
                  fieldName,
                  operator: ConstFilterOperator.eq,
                  value: predicate.value
              }
            : isFeedbackMessagePredicate(predicate)
            ? {
                  fieldName,
                  operator: predicate.operator,
                  value: predicate.defaultValue
              }
            : isActionsPredicate(predicate)
            ? {
                  fieldName,
                  operator: predicate.operator,
                  value: predicate.defaultValue
              }
            : {
                  fieldName,
                  operator: ConstFilterOperator.isOneOf,
                  value: predicate.value
              }
    }
}

function getAvailableFields<M>(
    fields: FilterFields<M>,
    value: FilterExpression<M>[]
): (keyof M)[] {
    return (
        Object.entries(fields) as [
            keyof M,
            FilterOption<M[keyof M]> | undefined
        ][]
    ).flatMap(([fieldName, option]) =>
        option?.allowMultiple ||
        value.every(expr => expr.fieldName !== fieldName)
            ? [fieldName]
            : []
    )
}
